/**
 * Google Analytics Utility
 * 
 * This utility provides helper functions to track events, page views, and user interactions
 * throughout the application using Google Analytics 4.
 */

// Extend Window interface to include gtag
declare global {
  interface Window {
    gtag: (
      command: string,
      targetId: string | Date,
      config?: Record<string, any>
    ) => void;
    dataLayer: any[];
  }
}

/**
 * Track a page view
 * @param path - The page path (e.g., '/dashboard')
 * @param title - The page title (optional)
 */
export const trackPageView = (path: string, title?: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', 'page_view', {
      page_path: path,
      page_title: title || document.title,
    });
  }
};

/**
 * Track a custom event
 * @param eventName - Name of the event (e.g., 'video_analysis_started')
 * @param eventParams - Additional parameters for the event
 */
export const trackEvent = (eventName: string, eventParams?: Record<string, any>) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('event', eventName, eventParams);
  }
};

/**
 * Track user sign up
 * @param method - Sign up method (e.g., 'google', 'email')
 */
export const trackSignUp = (method: string) => {
  trackEvent('sign_up', {
    method,
  });
};

/**
 * Track user login
 * @param method - Login method (e.g., 'google', 'email')
 */
export const trackLogin = (method: string) => {
  trackEvent('login', {
    method,
  });
};

/**
 * Track video analysis start
 * @param videoSource - Source of video (e.g., 'url', 'upload')
 * @param videoPlatform - Platform if URL (e.g., 'youtube', 'tiktok')
 */
export const trackVideoAnalysisStart = (videoSource: string, videoPlatform?: string) => {
  trackEvent('video_analysis_started', {
    video_source: videoSource,
    video_platform: videoPlatform,
  });
};

/**
 * Track video analysis completion
 * @param videoName - Name of the analyzed video
 * @param processingTime - Time taken to process (in seconds)
 */
export const trackVideoAnalysisComplete = (videoName: string, processingTime?: number) => {
  trackEvent('video_analysis_completed', {
    video_name: videoName,
    processing_time: processingTime,
  });
};

/**
 * Track recommendation generation
 * @param videoName - Name of the video
 */
export const trackRecommendationGenerated = (videoName: string) => {
  trackEvent('recommendation_generated', {
    video_name: videoName,
  });
};

/**
 * Track credit purchase
 * @param amount - Amount purchased
 * @param credits - Number of credits
 */
export const trackCreditPurchase = (amount: number, credits: number) => {
  trackEvent('purchase', {
    value: amount,
    currency: 'USD',
    items: [{
      item_name: 'Analysis Credits',
      quantity: credits,
      price: amount,
    }],
  });
};

/**
 * Track export action
 * @param exportType - Type of export (e.g., 'pdf', 'csv')
 * @param itemType - What was exported (e.g., 'analysis', 'report')
 */
export const trackExport = (exportType: string, itemType: string) => {
  trackEvent('export', {
    export_type: exportType,
    item_type: itemType,
  });
};

/**
 * Track share action
 * @param contentType - Type of content shared (e.g., 'analysis', 'report')
 * @param method - Share method (e.g., 'link', 'social')
 */
export const trackShare = (contentType: string, method: string) => {
  trackEvent('share', {
    content_type: contentType,
    method,
  });
};

/**
 * Track navigation to a specific page
 * @param pageName - Name of the page navigated to
 */
export const trackNavigation = (pageName: string) => {
  trackEvent('navigation', {
    page_name: pageName,
  });
};

/**
 * Track error
 * @param errorType - Type of error (e.g., 'api_error', 'validation_error')
 * @param errorMessage - Error message
 */
export const trackError = (errorType: string, errorMessage: string) => {
  trackEvent('error', {
    error_type: errorType,
    error_message: errorMessage,
  });
};

/**
 * Set user ID for tracking
 * @param userId - Unique user identifier
 */
export const setUserId = (userId: string) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('config', 'G-R3V7S5G5MF', {
      user_id: userId,
    });
  }
};

/**
 * Set user properties
 * @param properties - User properties to set
 */
export const setUserProperties = (properties: Record<string, any>) => {
  if (typeof window.gtag !== 'undefined') {
    window.gtag('set', 'user_properties', properties);
  }
};

const analytics = {
  trackPageView,
  trackEvent,
  trackSignUp,
  trackLogin,
  trackVideoAnalysisStart,
  trackVideoAnalysisComplete,
  trackRecommendationGenerated,
  trackCreditPurchase,
  trackExport,
  trackShare,
  trackNavigation,
  trackError,
  setUserId,
  setUserProperties,
};

export default analytics;

